import React from "react";
import PropTypes from "prop-types";

/**
 * US drug-marking symbols, drawn to inherit the surrounding text colour.
 *
 *  2 / 3 / 4 / 5  DEA controlled-substance mark — a capital C with the
 *                 schedule's Roman numeral inside (C-II … C-V), as required
 *                 on controlled-substance labelling
 *  -999           Rx legend mark, for prescription drugs that are not controlled
 *  otc            over-the-counter lettering
 *  prn            as-needed lettering
 *
 * These are the only symbols used for drug schedules in the app.
 *
 * `wide` gives lettering marks a wider canvas so their letters keep full
 * height at small sizes (used inside badges). The C marks are always square.
 */

// The open "C" ring, gap on the right.
const CRing = () => (
  <path
    d="M18.51 5.49 A9.2 9.2 0 1 0 18.51 18.51"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
  />
);

// Roman numeral "I".
const Bar = ({ x }) => (
  <line
    x1={x}
    y1="8.5"
    x2={x}
    y2="15.5"
    stroke="currentColor"
    strokeWidth="2.3"
    strokeLinecap="round"
  />
);

Bar.propTypes = { x: PropTypes.number.isRequired };

// Roman numeral "V".
const Vee = ({ x, half }) => (
  <polyline
    points={`${x - half},8.5 ${x},15.5 ${x + half},8.5`}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.3"
    strokeLinecap="round"
    strokeLinejoin="round"
  />
);

Vee.propTypes = { x: PropTypes.number.isRequired, half: PropTypes.number.isRequired };

const NUMERALS = {
  "2": (
    <>
      <Bar x={10.1} />
      <Bar x={13.9} />
    </>
  ),
  "3": (
    <>
      <Bar x={8.6} />
      <Bar x={12.0} />
      <Bar x={15.4} />
    </>
  ),
  "4": (
    <>
      <Bar x={9.0} />
      <Vee x={13.4} half={2.4} />
    </>
  ),
  "5": <Vee x={12.0} half={3.0} />
};

// size: font size on the square 24x24 canvas; wideSize: on the wide canvas.
const LETTERS = {
  "-999": { text: "Rx", size: 15, wideSize: 17, spacing: 0 },
  otc: { text: "OTC", size: 12.4, wideSize: 17, spacing: -0.6 },
  prn: { text: "PRN", size: 12.4, wideSize: 17, spacing: -0.6 }
};

const ScheduleSymbol = ({ token, size = 22, wide = false, className, title }) => {
  const key = String(token === undefined || token === null ? "" : token).toLowerCase();
  const numerals = NUMERALS[key];

  if (numerals) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        className={className}
        role="img"
        aria-label={title || key}
        focusable="false"
      >
        {title && <title>{title}</title>}
        <CRing />
        {numerals}
      </svg>
    );
  }

  const letters = LETTERS[key] || {
    text: key.toUpperCase().slice(0, 4),
    size: 12.4,
    wideSize: 17,
    spacing: -0.6
  };
  const canvasWidth = wide ? (letters.text.length >= 3 ? 38 : 28) : 24;

  return (
    <svg
      viewBox={`0 0 ${canvasWidth} 24`}
      width={(size * canvasWidth) / 24}
      height={size}
      className={className}
      role="img"
      aria-label={title || key}
      focusable="false"
    >
      {title && <title>{title}</title>}
      <text
        x={canvasWidth / 2}
        y={wide ? 12.8 : 12.6}
        textAnchor="middle"
        dominantBaseline="central"
        fill="currentColor"
        fontSize={wide ? letters.wideSize : letters.size}
        fontWeight="800"
        letterSpacing={wide ? 0 : letters.spacing}
        fontFamily="inherit"
      >
        {letters.text}
      </text>
    </svg>
  );
};

ScheduleSymbol.propTypes = {
  token: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.number,
  wide: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string
};

export default ScheduleSymbol;
